import React, { useMemo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import * as XLSX from 'xlsx';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { FullScreenLayout } from '../../routes/layouts/FullScreenLayout';
import { FullScreenHeader } from '@/components/shared/layout';
import { type CurriculumItem } from '../../data/classes';
import { useAuth } from '../../contexts/AuthContext';
import { ALL_PARTICIPANTS_ID } from './components/ParticipantSidebar';
import type { WinnerEntry } from '../results/Top3Winners';
import { EvalSummaryStatGrid } from '../results/EvalStatCard';
import {
    buildAssessmentEvalSummary,
    buildEvalSummaryFromTriageDto,
    buildSimulationSummary,
    mergeEvalSummary,
    mergeSimulationDisplayMetadata,
} from '../results/simulationResultBlocks';
import { triageDtosToPatientRows } from '@/utils/triageResultMapper';
import { useParticipantState, useFeedbackState, useSimulationFilters } from '../../hooks/useSimulationResults';
import { useSimulationResultsApi } from '@/hooks/useSimulationResultsApi';
import { useClassDetail, useClassUserList } from '../../data/queries/useClasses';
import type { Participant } from '../../data/participants';
import {
    PageFilters,
    SessionHeader,
    ResultsSidebar,
    ResultBody,
} from './resultSections';
import { normalizeResultViewTypeFromScenario } from './resultSections/ResultTableView';
import { resolveLsContentCodeForCoreNursingSession } from '@/data/scenarioCurriculumSheet';
import { classDetailDtoToClassItem } from '@/utils/classUtils';
import { ApiError } from '@/services/apiClient';

/**
 * 클래스 세션 평가결과 공통 페이지.
 * 관리자·마스터·학생 라우트 모두 이 컴포넌트만 사용하며, 본문 뷰는 `ResultBody`가 content_code·result_view에 맞게 분기한다.
 *
 * ## `studyResultOld.json` 과의 관계
 * - 이 JSON 파일은 **런타임에 import 되지 않음** — `docs/api/studyResultOld.json` = api2(실습기록) **엔드포인트·응답 예시 레퍼런스**이다.
 * - 앱이 실제로 쓰는 base URL: `getStudyResultPlatformBaseUrl()` (`.env`의 `REACT_APP_STUDY_RESULT_API_BASE_URL` / `REACT_APP_PLATFORM_API_BASE_URL` 등).
 *
 * ## 예: `/master/class-management/42/results/158`
 * - `classId=42` — 클래스, `sessionId=158` — **커리큘럼 상의 세션 `id`**. 시나리오 세션이면 이 값이 **시나리오 id**로도 쓰인다(`useSimulationResultsApi` → `GET /scenario/{id}/info`의 id).
 * - **웹 BFF(동일 오리진/프록시)**: `GET /class/42/detail`(상세+커리큘럼) · `GET /class/42/user-list`(마스터/어드만) ·
 *   `useSimulationResultsApi`가 `GET /scenario/{scenarioId}/info` · `GET /scenario-play-record?classId&scenarioId&…` — `buildStudyResultWebApiContextHeaders(42)`.
 * - **플랫폼 api2(≈ studyResultOld 문서)** — `useSimulationResultsApi` + `classLabPlatformService` + `scenarioPlatformPracticePaths` 가 호출. 문서 **§2.2** `class/lab/users/play-records/{classId}`(관리자·마스터일 때 보조) 등,
 *   **§4/§6** 류 `GET {lang}/scenario-md/…`·`scenario-acls/…` 등 **시나리오 ‘계열’에 따라 1개** — `inferScenarioPracticeFamily`·`fetchScenarioPracticePayload`.
 * - **api2 인증** — `getStudyResultPlatformAccessToken()` = `.env`의 `REACT_APP_STUDY_RESULT_ACCESS_TOKEN`(비어 있으면 `platformGet`이 웹 JWT 사용). **경로 userId 오버라이드**(로컬) — `REACT_APP_STUDY_RESULT_PLATFORM_PATH_USER_ID` → `studyResultPlatformAccess.ts`.
 * - **개발(`NODE_ENV=development`)** 에서 `useSimulationResultsApi` 완료 시 콘솔에 `[SimulationResults][BFF·백엔드(호출 주소)]` · `[Request 본문·쿼리]` · `[Response 본문]` 이 **항상** 출력(요약). 세부 BFF/플랫폼 `fetch` 턴 단위 로그는 `REACT_APP_DEBUG_STUDY_RESULT_API` 또는 `localStorage.setItem('debugStudyResultApi','1')` + 새로고침 — `studyResultDevConsoleLog.ts`.
 *
 * 실습·평가 수치·체크리스트: 플랫폼 응답만 `useSimulationResultsApi`로 표시(정적 시나리오 시트로 폴백하지 않음).
 * 더미 데모(문서 절·URL 나열): `/dev/lab-play-record-demo` (`LabPlayRecordDemo`).
 */
/** URL의 클래스·세션(·사용자)이 바뀌면 `SimulationResults` 를 리마운트해, 이전 세션의 플랫폼 `useSimulationResultsApi` state 잔상(잘못된 병합)을 막는다. */
export function SimulationResultsRoute(): React.ReactElement {
    const p = useParams<{ classId: string; sessionId: string; userId?: string }>();
    return <SimulationResults key={`${p.classId ?? ''}-${p.sessionId ?? ''}-${p.userId ?? ''}`} />;
}

export default function SimulationResults(): React.ReactElement {
    const { t, i18n } = useTranslation('class');
    const { classId, sessionId, userId: userIdParam } = useParams<{ classId: string; sessionId: string; userId?: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const { pathname } = location;
    const modeValueFromState = (location.state as { modeValue?: string } | null)?.modeValue;
    const isSingleUserView = Boolean(userIdParam);
    const { getCurrentRole, user } = useAuth();
    const role = getCurrentRole();
    const isManagerPath = pathname.includes('master') || pathname.includes('admin');
    const basePath = isManagerPath ? (role === 'master' ? '/master' : '/admin') : '';
    const fromClassManagement = pathname.includes('class-management');
    const backToClassDetailUrl = basePath
        ? `${basePath}/my-classes/${classId}${fromClassManagement ? '?from=class-management' : ''}`
        : `/student/my-classes/${classId}`;
    
    const [showPdfHeader, setShowPdfHeader] = useState(false);

    const numericClassId = classId ? parseInt(classId, 10) || undefined : undefined;
    const { data: apiClassDetailDto, isLoading: classLoading, isError: classDetailQueryFailed, error: classDetailQueryError } = useClassDetail(
        numericClassId
    );
    const { data: classUserListDto } = useClassUserList(
        numericClassId,
        { page: 1, pageSize: 500 },
        { enabled: Boolean(isManagerPath && numericClassId != null && numericClassId > 0) }
    );
    const classItem = useMemo(() => {
        if (apiClassDetailDto && classId && String(apiClassDetailDto.classId) === classId) {
            return classDetailDtoToClassItem(apiClassDetailDto);
        }
        return null;
    }, [apiClassDetailDto, classId]);
    const sessionItem = useMemo((): CurriculumItem | undefined => {
        if (!classItem?.curriculum) return undefined;
        const item = classItem.curriculum.find((s: CurriculumItem) => String(s.id) === sessionId);
        return item;
    }, [classItem, sessionId]);

    const classCategory = (classItem as { category?: string } | undefined)?.category;

    const participants: Participant[] = useMemo(() => {
        if (!isManagerPath) return [];
        const rows = classUserListDto?.userList;
        if (rows == null) return [];
        if (rows.length === 0) return [];
        return rows.map((u) => ({
            id: String(u.userId),
            name: `${u.realName?.trim() || u.loginId} (${u.loginId})`,
            department: [u.department, u.loginId].filter(Boolean).join(' · ') || '—',
        }));
    }, [isManagerPath, classUserListDto?.userList]);

    const participantsForView: Participant[] = useMemo(() => {
        if (!isSingleUserView || !userIdParam) return participants;
        const found = participants.find((p) => p.id === userIdParam);
        if (found) return participants;
        return [{ id: userIdParam, name: userIdParam, department: '—' }];
    }, [participants, isSingleUserView, userIdParam]);

    const initialSelectedId = isSingleUserView && userIdParam
        ? userIdParam
        : isManagerPath && participants.length > 0
            ? participants[0].id
            : (user?.userId != null ? user.userId.toString() : '');
    const listForSelection = isSingleUserView ? participantsForView : participants;
    const { selectedParticipantId, setSelectedParticipantId, selectedParticipant } = useParticipantState(listForSelection, initialSelectedId);
    const isAllParticipantsView = selectedParticipantId === ALL_PARTICIPANTS_ID;
    
    // 디버그 로그
    useEffect(() => {
    }, [selectedParticipantId, isAllParticipantsView]);

    useEffect(() => {
        if (isAllParticipantsView) return;
        if (listForSelection.length > 0 && !listForSelection.some((p) => p.id === selectedParticipantId)) {
            setSelectedParticipantId(listForSelection[0].id);
        }
    }, [listForSelection, selectedParticipantId, setSelectedParticipantId, isAllParticipantsView]);

    const {
        startDate,
        setStartDate,
        endDate,
        setEndDate,
        sortCriteria,
        setSortCriteria,
        completionDates: defaultCompletionDates,
        selectedCompletionDate,
        setSelectedCompletionDate,
    } = useSimulationFilters();

    /** 플랫폼 실습기록 — BFF /info `key`로 contentCode 보강(예: covid19 → 2801) */
    const platformResults = useSimulationResultsApi({
        classId,
        sessionItem,
        selectedParticipantId,
        isManagerPath,
        isAllParticipants: isAllParticipantsView,
        lang: i18n.language ?? 'ko',
        sortCriteria,
        selectedCompletionDate,
        startDate,
        endDate,
        modeValue: modeValueFromState ?? sessionItem?.modeValue,
    });
    const bffScenarioKey =
        (platformResults.scenarioInfo as { key?: string } | null | undefined)?.key ?? null;

    // Triage 카테고리 판단: classCategory 또는 bffScenarioKey가 'triage'
    const isTriageCategory = classCategory === 'triage' || bffScenarioKey === 'triage';
    
    // ACLS 카테고리 판단: classCategory 또는 bffScenarioKey가 'acls'
    const isAclsCategory = classCategory === 'acls' || bffScenarioKey === 'acls';
    
    // 전체 참가자 버튼 표시 조건
    // - 중증도분류: 관리자 + 연습 모드 (modeValue === '1')
    // - ACLS: 2차 개발 예정으로 현재 비활성화
    const showAllParticipants = 
        (isTriageCategory && isManagerPath && !isSingleUserView && platformResults.isTriagePracticeMode);
    
    // 디버그 로그
    
    useEffect(() => {
        if (bffScenarioKey === null) return;
        if (!showAllParticipants && isAllParticipantsView && listForSelection.length > 0) {
            console.log('⚠️ [전체 참가자 선택 강제 해제됨]');
            setSelectedParticipantId(listForSelection[0].id);
        }
    }, [showAllParticipants, isAllParticipantsView, listForSelection, setSelectedParticipantId, bffScenarioKey]);

    const resultMetadata = useMemo(() => {
        if (platformResults.error) return {};
        if (platformResults.loading) return {};
        const apiMeta = (platformResults.resultMetadata ?? {}) as Record<string, unknown>;
        const merged = mergeSimulationDisplayMetadata({}, apiMeta);
        
        // IV/채혈/COVID19/PPE 시나리오가 아닐 때만 triagePatients 추가
        const scenarioKey = (platformResults.scenarioInfo as { key?: string } | null)?.key;
        const scenarioType = apiMeta.scenarioType as string | undefined;
        const contentCode = apiMeta.content_code as string | undefined;
        const isIvScenario = scenarioKey === 'medication' || scenarioType === 'iv_injection';
        const isBloodDrawScenario = scenarioKey === 'diagnostic_test' || scenarioType === 'blood_draw';
        const isCovid19Scenario = scenarioKey === 'covid19' || scenarioType === 'covid19';
        const isPpeScenario = contentCode === 'ppe';
        
        if (!isIvScenario && !isBloodDrawScenario && !isCovid19Scenario && !isPpeScenario && platformResults.triageFromApi?.patients && platformResults.triageFromApi.patients.length > 0) {
            merged.triagePatients = triageDtosToPatientRows(platformResults.triageFromApi.patients);
        }
        
        return merged;
    }, [
        platformResults.error,
        platformResults.loading,
        platformResults.resultMetadata,
        platformResults.triageFromApi,
        platformResults.scenarioInfo,
    ]);

    const checklistItems = useMemo(() => platformResults.checklistItems, [platformResults.checklistItems]);

    const mustNotViolations = useMemo(() => {
        const items = checklistItems as { taskType?: string; status?: string }[];
        return items.filter((item) => item.taskType === 'Must-not' && item.status === 'fail').length;
    }, [checklistItems]);

    const sessionContentCode = (sessionItem as { contentCode?: string } | undefined)?.contentCode;
    const coreNursingLsFallback = resolveLsContentCodeForCoreNursingSession(classItem as { id?: string; title?: string; curriculum?: CurriculumItem[] }, sessionId ?? null);
    const contentCode = bffScenarioKey ?? sessionContentCode ?? coreNursingLsFallback ?? null;

    const bffEvaluationTypeRaw = (platformResults.scenarioInfo as { evaluationType?: unknown } | null | undefined)
        ?.evaluationType;
    const resultViewTypeFromBff =
        typeof bffEvaluationTypeRaw === 'string'
            ? normalizeResultViewTypeFromScenario(bffEvaluationTypeRaw)
            : undefined;

    const { expandedFeedback, toggleFeedback } = useFeedbackState();

    const sessionEvalSummaryStats = useMemo(() => {
        const fromMetadata = mergeEvalSummary(
            buildSimulationSummary(resultMetadata),
            buildAssessmentEvalSummary(resultMetadata),
        );
        
        // IV/채혈/COVID19/PPE 시나리오는 stats 배열을 사용하므로 triageFromApi 무시
        const scenarioKey = (platformResults.scenarioInfo as { key?: string } | null)?.key;
        const scenarioType = (resultMetadata as Record<string, unknown> | null)?.scenarioType as string | undefined;
        const contentCode = (resultMetadata as Record<string, unknown> | null)?.content_code as string | undefined;
        const isIvScenario = scenarioKey === 'medication' || scenarioType === 'iv_injection';
        const isBloodDrawScenario = scenarioKey === 'diagnostic_test' || scenarioType === 'blood_draw';
        const isCovid19Scenario = scenarioKey === 'covid19' || scenarioType === 'covid19';
        const isPpeScenario = contentCode === 'ppe';
        
        if (isIvScenario || isBloodDrawScenario || isCovid19Scenario || isPpeScenario || !platformResults.triageFromApi) return fromMetadata;
        return mergeEvalSummary(buildEvalSummaryFromTriageDto(platformResults.triageFromApi), fromMetadata);
    }, [resultMetadata, platformResults.triageFromApi, platformResults.scenarioInfo]);

    /** API에서 가져온 winners를 WinnerEntry 형태로 변환 */
    const top3Winners: WinnerEntry[] = useMemo(() => {
        const apiWinners = platformResults.winnersFromApi || [];
        
        return apiWinners.slice(0, 3).map((winner, index) => {
            // winner가 문자열(이메일)인 경우
            if (typeof winner === 'string') {
                const emailOrId = winner;
                const atIndex = emailOrId.indexOf('@');
                const nickname = atIndex > 0 ? emailOrId.slice(0, atIndex) : emailOrId;
                return {
                    rank: (index + 1) as 1 | 2 | 3,
                    nickname,
                    email: emailOrId,
                    score: 0, // 기존 프로젝트는 이메일만 있는 경우가 있음
                };
            }
            // winner가 객체인 경우
            const winnerObj = winner as { nickname?: string; user_email?: string; email?: string; score?: number };
            const email = winnerObj.user_email || winnerObj.email || '';
            const nickname = winnerObj.nickname || email.split('@')[0] || `Winner ${index + 1}`;
            return {
                rank: (index + 1) as 1 | 2 | 3,
                nickname,
                email,
                score: winnerObj.score || 0,
            };
        });
    }, [platformResults.winnersFromApi]);

    const completionDates = useMemo(
        () =>
            platformResults.completionDatesFromApi.length > 0
                ? platformResults.completionDatesFromApi
                : defaultCompletionDates,
        [platformResults.completionDatesFromApi, defaultCompletionDates],
    );

    // API에서 가져온 completionDates가 업데이트되면 첫 번째 날짜를 자동 선택
    // 참가자가 바뀌면 completionDates도 바뀌므로 항상 최신 날짜로 업데이트
    useEffect(() => {
        if (completionDates.length > 0) {
            setSelectedCompletionDate(completionDates[0]);
        }
    }, [completionDates, setSelectedCompletionDate]);

    const handleDownloadExcel = () => {
        if (!selectedParticipant || !checklistItems) return;
        const sr = (key: string) => t(`simulationResults.${key}`);
        const metaStats = (resultMetadata as { stats?: { label: string; value?: string }[] } | null | undefined)?.stats;

        const summaryData = [
            { [sr('colItem')]: sr('colEvaluator'), [sr('colContent')]: `${selectedParticipant.name} (${selectedParticipant.id})` },
            { [sr('colItem')]: sr('colAffiliation'), [sr('colContent')]: selectedParticipant.department },
            { [sr('colItem')]: sr('colScenario'), [sr('colContent')]: sessionItem?.name || sr('unknown') },
            { [sr('colItem')]: sr('colEvaluatedAt'), [sr('colContent')]: selectedCompletionDate },
            { [sr('colItem')]: sr('colScore'), [sr('colContent')]: `${metaStats?.find((s) => s.label === '총점')?.value || '0'} / 100` },
            { [sr('colItem')]: sr('colSuccessRate'), [sr('colContent')]: `${metaStats?.find((s) => s.label === '정답율' || s.label === '성공률')?.value || '0'}%` },
            { [sr('colItem')]: sr('mustNotViolations'), [sr('colContent')]: `${mustNotViolations}${sr('times')}` },
        ];

        const typeKey = (type: string) => type === 'performance' ? 'typePerformance' : type === 'assessment' ? 'typeAssessment' : type === 'equipment' ? 'typeEquipment' : 'typeProcedure';
        const detailedData = checklistItems.map((item: any, index: number) => ({
            [sr('colNo')]: index + 1,
            [sr('colType')]: sr(typeKey(item.evaluationType)),
            [sr('colEvent')]: item.eventName,
            [sr('colTask')]: item.taskName,
            [sr('colDetail')]: item.description,
            [sr('colScore')]: item.rowScoreUnspecified ? '—' : item.score >= 0 ? `+${item.score}` : item.score,
            [sr('colPerformanceResult')]: item.status.toUpperCase(),
            [sr('colSelectedValue')]: item.value || '-',
            [sr('colCorrectValue')]: item.correctValue || '-',
            [sr('colFeedback')]: item.feedback || '-',
        }));

        const wb = XLSX.utils.book_new();
        const wsSummary = XLSX.utils.json_to_sheet(summaryData);
        const wsDetailed = XLSX.utils.json_to_sheet(detailedData);
        XLSX.utils.book_append_sheet(wb, wsSummary, sr('summarySheet'));
        XLSX.utils.book_append_sheet(wb, wsDetailed, sr('detailedSheet'));
        const fileName = `${selectedParticipant.name}_${sessionItem?.name || sr('exportFileName')}.xlsx`;
        XLSX.writeFile(wb, fileName);
    };

    const findLoginId = useMemo(() => {
        return (userId: string): string => {
            if (!classUserListDto?.userList) return '-';
            const user = classUserListDto.userList.find((u) => String(u.userId) === userId);
            return user?.loginId || '-';
        };
    }, [classUserListDto]);

    const hasPlayData = useMemo(() => {
        const practiceObj = platformResults.practicePayload as Record<string, unknown> | null | undefined;
        const hasLatestScenario = practiceObj?.latestScenario != null;
        const hasHighestScenario = practiceObj?.highestScenario != null;
        const hasPlayRecords = platformResults.playRecords.length > 0;
        return hasLatestScenario || hasHighestScenario || hasPlayRecords;
    }, [platformResults.practicePayload, platformResults.playRecords]);

    const handleDownloadPdf = async () => {
        try {
            setShowPdfHeader(true);
            await new Promise(resolve => setTimeout(resolve, 100));

            const captureElement = document.querySelector('.result-capture-area');
            if (!captureElement) {
                console.error('캡처할 영역을 찾을 수 없습니다.');
                setShowPdfHeader(false);
                return;
            }

            const canvas = await html2canvas(captureElement as HTMLElement, {
                scale: 2,
                useCORS: true,
                logging: false,
                backgroundColor: '#ffffff',
                windowWidth: 1440,
            });

            const imgData = canvas.toDataURL('image/png');
            const pdf = new jsPDF('p', 'mm', 'a4');
            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();
            const margin = 10;
            const contentWidth = pageWidth - margin * 2;

            const imgWidth = contentWidth;
            const imgHeight = (canvas.height * contentWidth) / canvas.width;

            if (imgHeight <= pageHeight - margin * 2) {
                pdf.addImage(imgData, 'PNG', margin, margin, imgWidth, imgHeight);
            } else {
                let remainingHeight = imgHeight;
                let sourceY = 0;
                let pageCount = 0;

                while (remainingHeight > 0) {
                    if (pageCount > 0) {
                        pdf.addPage();
                    }

                    const availableHeight = pageHeight - margin * 2;
                    const sourceHeight = (availableHeight * canvas.width) / contentWidth;

                    const canvasSlice = document.createElement('canvas');
                    canvasSlice.width = canvas.width;
                    canvasSlice.height = Math.min(sourceHeight, canvas.height - sourceY);
                    const ctx = canvasSlice.getContext('2d');
                    if (ctx) {
                        ctx.drawImage(
                            canvas,
                            0,
                            sourceY,
                            canvas.width,
                            canvasSlice.height,
                            0,
                            0,
                            canvas.width,
                            canvasSlice.height
                        );
                    }

                    const sliceData = canvasSlice.toDataURL('image/png');
                    const sliceHeight = (canvasSlice.height * contentWidth) / canvas.width;
                    pdf.addImage(sliceData, 'PNG', margin, margin, imgWidth, sliceHeight);

                    sourceY += canvasSlice.height;
                    remainingHeight -= availableHeight;
                    pageCount++;
                }
            }

            const studentName = isAllParticipantsView 
                ? '전체참가자' 
                : selectedParticipant?.name || '-';
            const fileName = `${studentName}_${sessionItem?.name || '결과'}_${selectedCompletionDate || new Date().toISOString().split('T')[0]}.pdf`;
            pdf.save(fileName);

            setShowPdfHeader(false);
        } catch (error) {
            console.error('PDF 생성 중 오류 발생:', error);
            setShowPdfHeader(false);
        }
    };

    if (classId && classLoading) {
        return (
            <FullScreenLayout
                backgroundColor="bg-white"
                header={<FullScreenHeader title={<>{t('simulationResults.title')}</>} onBack={() => navigate(backToClassDetailUrl)} />}
            >
                <div className="flex flex-1 items-center justify-center p-8 text-gray-500">{t('simulationResults.loading')}</div>
            </FullScreenLayout>
        );
    }

    if (numericClassId && !classLoading && classDetailQueryFailed && !apiClassDetailDto) {
        const st = classDetailQueryError instanceof ApiError ? classDetailQueryError.status : null;
        const body =
            st === 403
                ? '이 클래스에 접근할 권한이 없거나(403) 백엔드 정책에 막혔습니다. 기관·클래스 소유/참가 여부를 확인해 주세요.'
                : st === 401
                  ? '인증이 필요합니다(401). 다시 로그인한 뒤 시도해 주세요.'
                  : (classDetailQueryError instanceof Error && classDetailQueryError.message) || '클래스 정보를 불러오지 못했습니다.';
        return (
            <FullScreenLayout
                backgroundColor="bg-white"
                header={<FullScreenHeader title={<>{t('simulationResults.title')}</>} onBack={() => navigate(backToClassDetailUrl)} />}
            >
                <div className="p-8 space-y-2 text-gray-800">
                    <p className="font-medium text-red-700">클래스를 불러오지 못했습니다.</p>
                    <p className="text-sm text-gray-600">{body}</p>
                </div>
            </FullScreenLayout>
        );
    }

    if (!classItem || !sessionItem) {
        return (
            <FullScreenLayout
                backgroundColor="bg-white"
                header={<FullScreenHeader title={<>{t('simulationResults.title')}</>} onBack={() => navigate(backToClassDetailUrl)} />}
            >
                <div className="p-8 space-y-2 text-gray-800">
                    <p>{t('simulationResults.sessionNotFound')}</p>
                    {apiClassDetailDto ? null : (
                        <p className="text-sm text-gray-500">
                            클래스 id <code className="text-xs bg-gray-100 px-1 rounded">{classId}</code> / 세션{' '}
                            <code className="text-xs bg-gray-100 px-1 rounded">{sessionId}</code> 커리큘럼에 없으면 이 메시지가 뜰 수 있습니다.
                        </p>
                    )}
                </div>
            </FullScreenLayout>
        );
    }

    const headerRightContent = (
        <div className="flex items-center gap-3">
            <PageFilters
                startDate={startDate}
                endDate={endDate}
                onStartDateChange={setStartDate}
                onEndDateChange={setEndDate}
                sortCriteria={sortCriteria}
                onSortCriteriaChange={setSortCriteria}
                onDownloadExcel={handleDownloadExcel}
                onDownloadPdf={handleDownloadPdf}
                showPdfDownload={isManagerPath && hasPlayData}
            />
        </div>
    );

    /** 플랫폼·BFF 응답을 붙이기 전엔 정적 점수·`예정` 절차 스켈이 잠깐 보이는 "잔상"이 있어, 이 구간엔 본문·요약을 렌더하지 않는다. (비디오 세션은 스킵) */
    const suppressResultPlaceholder = Boolean(platformResults.loading);

    return (
        <FullScreenLayout
            backgroundColor="bg-white"
            header={
                <FullScreenHeader
                    title={
                        (classItem as { title?: string })?.title
                            ? <><span className="text-brand-500">{(classItem as { title?: string }).title}</span><span> {t('simulationResults.title')}</span></>
                            : t('simulationResults.title')
                    }
                    onBack={() => navigate(backToClassDetailUrl)}
                    rightContent={headerRightContent}
                />
            }
        >
            <div className="flex flex-1 min-h-0 overflow-hidden">
                {isManagerPath && !isSingleUserView && (
                    <ResultsSidebar
                        participants={participants}
                        selectedParticipantId={selectedParticipantId}
                        onSelectParticipant={setSelectedParticipantId}
                        showAllParticipants={showAllParticipants}
                    />
                )}

                <div className="flex-1 min-h-0 overflow-y-auto bg-white text-gray-900">
                    <div className="max-w-6xl mx-auto px-4 py-6 md:px-8 md:py-8 space-y-6 md:space-y-8">
                        {platformResults.error && (
                            <p className="text-sm text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-4 py-3" role="alert">
                                {platformResults.error}
                            </p>
                        )}
                        <div className="result-capture-area space-y-6 md:space-y-8">
                            {showPdfHeader && (
                                <div className="bg-white border-b-2 border-gray-200 pb-4 mb-4">
                                    <div className="flex items-center justify-between mb-4">
                                        <img 
                                            src="/assets/images/brand/newbase.png" 
                                            alt="Newbase" 
                                            className="h-8 object-contain"
                                        />
                                        <div className="text-xs text-gray-500">
                                            {new Date().toLocaleDateString(i18n.language)}
                                        </div>
                                    </div>
                                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm space-y-2">
                                        <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                                            <div>
                                                <span className="font-semibold text-gray-700">{t('simulationResults.pdfHeaderClassName')}:</span>
                                                <span className="ml-2 text-gray-900">{classItem?.title || '-'}</span>
                                            </div>
                                            <div>
                                                <span className="font-semibold text-gray-700">{t('simulationResults.pdfHeaderScenario')}:</span>
                                                <span className="ml-2 text-gray-900">{sessionItem?.name || '-'}</span>
                                            </div>
                                            <div>
                                                <span className="font-semibold text-gray-700">{t('simulationResults.pdfHeaderOrganization')}:</span>
                                                <span className="ml-2 text-gray-900">{classItem?.organizationName || '-'}</span>
                                            </div>
                                            <div>
                                                <span className="font-semibold text-gray-700">{t('simulationResults.pdfHeaderStudentName')}:</span>
                                                <span className="ml-2 text-gray-900">
                                                    {isAllParticipantsView ? t('shared.allParticipants') : selectedParticipant?.name || '-'}
                                                </span>
                                            </div>
                                            <div>
                                                <span className="font-semibold text-gray-700">{t('simulationResults.pdfHeaderStudentId')}:</span>
                                                <span className="ml-2 text-gray-900">
                                                    {isAllParticipantsView ? '-' : findLoginId(selectedParticipantId)}
                                                </span>
                                            </div>
                                            <div>
                                                <span className="font-semibold text-gray-700">{t('simulationResults.pdfHeaderPracticeDate')}:</span>
                                                <span className="ml-2 text-gray-900">{selectedCompletionDate || '-'}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                            <SessionHeader
                                sessionItem={sessionItem}
                                isAllParticipantsView={isAllParticipantsView}
                                selectedParticipant={selectedParticipant}
                                authUser={user}
                                sortCriteria={sortCriteria}
                                completionDates={completionDates}
                                selectedCompletionDate={selectedCompletionDate}
                                onCompletionDateChange={setSelectedCompletionDate}
                            />

                            {suppressResultPlaceholder ? (
                                <div
                                    className="min-h-[min(60vh,520px)] flex items-center justify-center"
                                    role="status"
                                    aria-live="polite"
                                    aria-busy="true"
                                >
                                    <p className="text-sm text-gray-500">{t('simulationResults.loading')}</p>
                                </div>
                            ) : (
                                <>
                                    {/* 플레이 기록이 없을 때 - practicePayload의 latestScenario나 playRecords 확인 */}
                                    {(() => {
                                        const practiceObj = platformResults.practicePayload as Record<string, unknown> | null | undefined;
                                        const hasLatestScenario = practiceObj?.latestScenario != null;
                                        const hasHighestScenario = practiceObj?.highestScenario != null;
                                        const hasPlayRecords = platformResults.playRecords.length > 0;
                                        const hasAnyData = hasLatestScenario || hasHighestScenario || hasPlayRecords;
                                        
                                        if (!hasAnyData) {
                                            return (
                                                <div className="min-h-[min(60vh,520px)] flex flex-col items-center justify-center gap-4">
                                                    <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center">
                                                        <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                        </svg>
                                                    </div>
                                                    <div className="text-center">
                                                        <p className="text-lg font-medium text-gray-900 mb-1">
                                                            플레이 기록이 없습니다
                                                        </p>
                                                        <p className="text-sm text-gray-500">
                                                            시나리오를 플레이한 후 결과를 확인할 수 있습니다
                                                        </p>
                                                    </div>
                                                </div>
                                            );
                                        }
                                        
                                        return (
                                            <>
                                                <EvalSummaryStatGrid stats={sessionEvalSummaryStats} isAllParticipantsView={isAllParticipantsView} />
                                                <ResultBody
                                                    resultViewContext={{
                                                        contentCode,
                                                    resultViewType: resultViewTypeFromBff ?? null,
                                                        classCategory,
                                                    }}
                                                    resultMetadata={resultMetadata as Record<string, unknown> | null | undefined}
                                                    isAllParticipantsView={isAllParticipantsView}
                                                    winners={top3Winners}
                                                    checklistItems={checklistItems as unknown[]}
                                                    expandedFeedback={expandedFeedback}
                                                    onToggleFeedback={toggleFeedback}
                                                />
                                            </>
                                        );
                                    })()}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </FullScreenLayout>
    );
}
